/*
 * WHERE A SIGNUP CAME FROM (2026-10-20): remember, from the first moment someone lands on the app, how they got here, so the signup form can report it and the admin Growth page can say which sources
 * produce customers. Chris's goal is 100 paying customers; knowing which door they came through is the question the Growth page could not answer.
 *
 * WHAT IS REMEMBERED: the campaign tags on the landing address (utm_source, utm_medium, utm_campaign) and the HOST of the page that linked here (or the host in a `ref` parameter, which the marketing site adds to its signup links to carry the visitor's ORIGINAL source across) (never the full address, which can carry a query string
 * or a name). First touch wins, so the visit that brought them in is the one that is counted, not the signup page they happened to be on when they finished: a landing WITH a campaign source replaces a
 * remembered one that only had a referrer (a tagged link is the better evidence), nothing else does. A memory older than 30 days is forgotten. It lives in this browser only (localStorage) until the
 * signup form sends it, and the SERVER cleans whatever arrives (UserApi/Helper/signupSource.ts): this module is a convenience, not a trust boundary.
 *
 * It can never get in the way: every storage call is guarded, and `getSignupSource()` always returns an object, empty when nothing is known (a direct visit), because "nothing reported" is a fact too.
 */
export interface SignupSourceReport { source?: string; medium?: string; campaign?: string; referrerHost?: string }
export interface Remembered extends SignupSourceReport { at: number }

export const STORAGE_KEY = "skydive.signupSource";
export const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_LEN = 64;

const tag = (raw: string | null | undefined): string | undefined => {
  if (!raw) return undefined;
  const v = raw.toLowerCase().replace(/[^a-z0-9 _.+-]/g, "").replace(/\s+/g, " ").trim().slice(0, MAX_LEN).trim();

  return v || undefined;
};

/** The host of a referrer address, or undefined when there is none, it is unparseable, or it is the app itself (moving between the app's own pages is not a source). */
export const referrerHostOf = (referrer: string, ownHost: string): string | undefined => {
  if (!referrer) return undefined;
  try {
    const host = new URL(referrer).hostname.toLowerCase();

    return host && host !== ownHost.toLowerCase() ? host : undefined;
  } catch (e) {
    return undefined;
  }
};

const HOST_PATTERN = /^(?=.{1,100}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$|^localhost$/;

/** A host passed in the address as `ref` (the marketing site forwards the site that sent the visitor to IT): kept only when it is a plain host name, never an address, never anything else. */
export const hostFromRefParam = (raw: string | null | undefined): string | undefined => {
  if (!raw) return undefined;
  const v = raw.trim().toLowerCase();

  return HOST_PATTERN.test(v) ? v : undefined;
};

/** What this landing says about how the person arrived: the campaign tags and the linking site. Undefined when it says nothing (a direct visit, or moving around inside the app). */
export const readLanding = (search: string, referrer: string, ownHost: string, now: number): Remembered | undefined => {
  let params: URLSearchParams;
  try { params = new URLSearchParams(search); } catch (e) { params = new URLSearchParams(); }
  const found: SignupSourceReport = {};
  // `ref` (forwarded by the marketing site) is the site that really sent the visitor; the referrer this page sees is then just the marketing site itself, so `ref` wins when it is there.
  const source = tag(params.get("utm_source")); const medium = tag(params.get("utm_medium")); const campaign = tag(params.get("utm_campaign")); const referrerHost = hostFromRefParam(params.get("ref")) ?? referrerHostOf(referrer, ownHost);
  if (source) found.source = source;
  if (medium) found.medium = medium;
  if (campaign) found.campaign = campaign;
  if (referrerHost) found.referrerHost = referrerHost;

  return Object.keys(found).length ? { ...found, at: now } : undefined;
};

const fresh = (r: Remembered | undefined | null, now: number): Remembered | undefined => (r && typeof r.at === "number" && now - r.at <= MAX_AGE_MS && now >= r.at ? r : undefined);

/** First touch wins; a tagged landing beats a remembered one that has no campaign source; an expired memory is as good as none. */
export const mergeFirstTouch = (stored: Remembered | undefined | null, landing: Remembered | undefined, now: number): Remembered | undefined => {
  const kept = fresh(stored, now);
  if (!landing) return kept;
  if (!kept) return landing;
  if (landing.source && !kept.source) return landing;

  return kept;
};

/** What the signup form sends: the remembered report without its timestamp. Always an object. */
export const reportOf = (r: Remembered | undefined): SignupSourceReport => {
  if (!r) return {};
  const { at, ...report } = r;
  void at;

  return report;
};

const load = (): Remembered | undefined => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    return raw ? (JSON.parse(raw) as Remembered) : undefined;
  } catch (e) {
    return undefined;
  }
};

/** Call once as the app starts (and it is safe to call again): notes how this visit arrived. */
export const captureSignupSource = (): void => {
  try {
    const now = Date.now();
    const merged = mergeFirstTouch(load(), readLanding(window.location.search, document.referrer, window.location.hostname, now), now);
    if (merged) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* storage blocked or full: signing up must never depend on this */
  }
};

/** For the signup form: how this person arrived, as far as this browser knows. */
export const getSignupSource = (): SignupSourceReport => reportOf(fresh(load(), Date.now()));
